import {
  EpisodicEventsForManifestQuery,
  PickableEpisodicEventFragment,
  WasteEpisodicEventStatus,
  WasteEpisodicEventType,
} from "generated-graphql/graphql";

import { episodicWindowEnd } from "./episodicDisplay";
import { reliefAppliesToDeclarations } from "./manifestExemptionForm";

export type ManifestForEpisodic = {
  processedManifestId: string;
  manifestTrackingNumber: string;
  facilityId: string;
  shippedDate: string;
};

export type PickableEvent = {
  id: string;
  facilityId: string;
  facilityName: string;
  type: WasteEpisodicEventType;
  status: WasteEpisodicEventStatus;
  reason: string;
  startDate: string;
  endDate: string;
  /** Whether pounds declared on this event leave generator status; false in a non-adopting state. */
  reliefApplies: boolean;
};

export function eventsCoveringShipDate(
  events: PickableEvent[],
  shippedDate: string
): PickableEvent[] {
  return events.filter((event) => {
    const end = episodicWindowEnd(event.startDate);
    return end !== null && event.startDate <= shippedDate && shippedDate <= end;
  });
}

export type EventEligibility =
  | { kind: "checking" }
  | { kind: "eligible" }
  | { kind: "ineligible"; reason: string };

export const ELIGIBILITY_CHECKING: EventEligibility = { kind: "checking" };
export const ELIGIBILITY_UNCHECKED: EventEligibility = {
  kind: "ineligible",
  reason: "This event could not be checked",
};

/** The server's verdict on one event: the manifest must come back as a candidate inside the window. */
export function eligibilityFromCandidates(
  candidates: { manifestTrackingNumber: string; inSuggestionWindow: boolean }[],
  manifestTrackingNumber: string
): EventEligibility {
  const candidate = candidates.find((c) => c.manifestTrackingNumber === manifestTrackingNumber);
  if (!candidate) {
    return { kind: "ineligible", reason: "Not a manifest at this event's facility" };
  }
  if (!candidate.inSuggestionWindow) {
    return { kind: "ineligible", reason: "Shipped outside this event's 60-day window" };
  }
  return { kind: "eligible" };
}

export function declareDefaultsForManifest(
  manifest: ManifestForEpisodic,
  wasteCodes: string[]
): { startDate: string; reason: string } {
  const codes = [...new Set(wasteCodes)].sort();
  const suffix = codes.length > 0 ? ` (${codes.join(", ")})` : "";
  return {
    startDate: manifest.shippedDate,
    reason: `Waste shipped on manifest ${manifest.manifestTrackingNumber}${suffix}`,
  };
}

type PickableFacility = {
  name: string;
  episodicEvents?: { items: PickableEpisodicEventFragment[] } | null;
};

function toPickable(
  event: PickableEpisodicEventFragment,
  facility: PickableFacility
): PickableEvent {
  return {
    id: event.id,
    facilityId: event.facilityId,
    facilityName: facility.name,
    type: event.type,
    status: event.status,
    reason: event.reason,
    startDate: String(event.startDate),
    endDate: String(event.endDate),
    reliefApplies: reliefAppliesToDeclarations(event.relief),
  };
}

export function episodicEventsForManifestVariables(facilityId: string, tenantId: string) {
  return {
    facilityId,
    membersSearch: `tenantId:${tenantId} colocationWasteFacilityId:${facilityId}`,
  };
}

/**
 * The manifest's facility is the colocation primary, so every listed event counts its lines.
 * Whether a given manifest qualifies at an event is the server's call, asked per event.
 */
export function pickableEvents(data: EpisodicEventsForManifestQuery): PickableEvent[] {
  const primary = data.facility;
  const facilities: PickableFacility[] = [...(primary ? [primary] : []), ...data.facilities.items];
  return facilities
    .flatMap((facility) =>
      (facility.episodicEvents?.items ?? []).map((event) => toPickable(event, facility))
    )
    .sort((a, b) => b.startDate.localeCompare(a.startDate));
}

type WasteCodeList = (string | null)[] | null | undefined;

export function wasteCodesOnLines(
  lines: { federalWasteCodes?: WasteCodeList; stateWasteCodes?: WasteCodeList }[]
): string[] {
  const codes = lines.flatMap((line) => [
    ...(line.federalWasteCodes ?? []),
    ...(line.stateWasteCodes ?? []),
  ]);
  return [...new Set(codes.filter((code): code is string => !!code))];
}

export const MANIFEST_SIDE_REFETCH_QUERIES = [
  "WasteLinesUnified",
  "ProcessedManifests",
  "ShippingPaperwork",
];
